import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { Coffee, RefreshCw } from 'lucide-react'
import { apiFetch } from '../../config/api'
import { type PageStat, StatTable } from './statistikShared'

export default function DashboardStatistikBrunch() {
  const [pages, setPages] = useState<PageStat[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const getAuthToken = () => localStorage.getItem('dashboard_token') || ''

  const fetchStats = async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await apiFetch('/analytics/pages', {
        headers: { Authorization: getAuthToken() },
      })
      if (!res.ok) {
        if (res.status === 401) return
        throw new Error('Statistiken konnten nicht geladen werden.')
      }
      const data = await res.json()
      if (data.success && Array.isArray(data.pages)) {
        const all = data.pages.map((p: { path: string; today: string | number; last7d: string | number }) => ({
          path: p.path || '/',
          today: String(p.today ?? 0),
          last7d: String(p.last7d ?? 0),
        }))
        setPages(all.filter((p: PageStat) => (p.path || '').startsWith('/brunch')))
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unbekannter Fehler')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchStats()
  }, [])

  const brunchToday = pages.reduce((s, p) => s + (parseInt(p.today, 10) || 0), 0)
  const brunch7d = pages.reduce((s, p) => s + (parseInt(p.last7d, 10) || 0), 0)
  const max7dBrunch = Math.max(1, ...pages.map((p) => parseInt(p.last7d, 10) || 0))

  return (
    <div>
      <motion.div
        className="mb-6 sm:mb-8 md:mb-10"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 min-w-0">
          <div className="flex-1 min-w-0">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2 }}
              className="inline-flex items-center gap-2 bg-emerald-500/20 border border-emerald-500/30 rounded-full px-3 py-2 sm:px-4 mb-3 sm:mb-4"
            >
              <Coffee className="w-4 h-4 text-emerald-400 shrink-0" />
              <span className="text-xs sm:text-sm font-medium text-emerald-400">Brunch</span>
            </motion.div>
            <h1 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-light text-white mb-2 sm:mb-3 bg-clip-text text-transparent bg-gradient-to-r from-white via-white to-emerald-400 break-words">
              Brunch
            </h1>
            <p className="text-gray-300 text-base sm:text-lg mb-4">
              Wie oft werden Brunch-Seiten angeklickt?
            </p>
            <div className="h-1 w-32 bg-gradient-to-r from-emerald-500 to-emerald-400 rounded-full shadow-lg shadow-emerald-500/30" />
          </div>
          <motion.button
            onClick={fetchStats}
            disabled={loading}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/5 hover:bg-emerald-500/20 border border-white/10 hover:border-emerald-500/30 text-gray-300 hover:text-emerald-400 transition-all disabled:opacity-50"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Aktualisieren
          </motion.button>
        </div>
      </motion.div>

      {error && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="mb-6 p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-400"
        >
          {error}
        </motion.div>
      )}

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3 }}
        className="rounded-xl sm:rounded-2xl bg-gradient-to-br from-gray-900/90 to-gray-900/90 backdrop-blur-xl border border-emerald-500/20 overflow-hidden min-w-0"
      >
        <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-emerald-500/20 flex flex-col sm:flex-row sm:flex-wrap items-start sm:items-center justify-between gap-3 sm:gap-4 bg-emerald-500/5">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-emerald-500/20">
              <Coffee className="w-5 h-5 text-emerald-400" />
            </div>
            <div className="min-w-0">
              <h2 className="font-serif text-lg sm:text-xl font-light text-white">Brunch-Seiten</h2>
              <p className="text-gray-400 text-xs sm:text-sm">Aufrufe pro Seite (Übersicht und Brunch-Details)</p>
            </div>
          </div>
          <div className="flex items-center gap-4 sm:gap-6">
            <div className="text-right">
              <p className="text-xl sm:text-2xl font-bold text-white tabular-nums">{loading ? '—' : brunchToday}</p>
              <p className="text-xs text-gray-400">Heute</p>
            </div>
            <div className="w-px h-8 sm:h-10 bg-white/10" />
            <div className="text-right">
              <p className="text-xl sm:text-2xl font-bold text-emerald-400 tabular-nums">{loading ? '—' : brunch7d}</p>
              <p className="text-xs text-gray-400">7 Tage</p>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="p-8 sm:p-12 text-center text-gray-400">
            <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-3 opacity-70" />
            <p>Statistiken werden geladen …</p>
          </div>
        ) : pages.length === 0 ? (
          <div className="px-4 sm:px-6 py-6 sm:py-8 text-center text-gray-500 text-sm">
            Noch keine Aufrufe von Brunch-Seiten.
          </div>
        ) : (
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <StatTable rows={pages} maxBar={max7dBrunch} startIndex={0} />
          </div>
        )}
      </motion.div>
    </div>
  )
}
